import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useLogin } from '../hooks/useLogin';
import '../auth.css';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import { storage } from '../utils/storage';
import { getRoleRedirectPath } from '../utils/userUtils';

export default function DealerLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { login, loading, error } = useLogin();

  const { values, handleChange, handleSubmit } = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: async formData => {
      // In a real application, you might pass a role type or hit a specific dealer login endpoint
      await login(formData);
    },
  });

  return (
    <div className="auth-page-light home-page-light">
      <MainHeader />
      
      <main className="auth-main">
        <div className="auth-visual-bg"></div>
        
        <div className="auth-container">
          <div className="auth-glass-card">
            {/* Form Side */}
            <div className="auth-form-side">
              <div className="auth-nav-tabs">
                <button className="auth-nav-link active">{t('dealer_login.tab_login')}</button>
                <button className="auth-nav-link" onClick={() => navigate('/dealer-register')}>{t('dealer_login.tab_register')}</button>
              </div>

              <h2>{t('dealer_login.title')}</h2>
              <p className="auth-subtitle">{t('dealer_login.subtitle')}</p>

              {error && <div className="alert alert-danger border-0 rounded-3 mb-4">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label">{t('dealer_login.email')}</label>
                  <input
                    type="email"
                    className="auth-input"
                    placeholder={t('dealer_login.email_placeholder')}
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">{t('dealer_login.password')}</label>
                    <Link to="/forgot-password" style={{ fontSize: '0.85rem' }} className="auth-link">
                      {t('dealer_login.forgot_password')}
                    </Link>
                  </div>
                  <div className="position-relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="auth-input"
                      placeholder={t('dealer_login.password_placeholder')}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3"
                      style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.4)' }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </span>
                  </div>
                </div>

                <button type="submit" className="btn-auth-submit" disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : null}
                  {loading ? t('dealer_login.authenticating') : t('dealer_login.submit')}
                </button>
              </form>

              <div className="auth-footer-text">
                {t('dealer_login.not_dealer')} <Link to="/login" className="auth-link">{t('dealer_login.user_login')}</Link>
                <div className="mt-3 pt-3 border-top" style={{ fontSize: '0.85rem' }}>
                  {t('dealer_login.become_dealer')} <Link to="/dealer-register" className="auth-link">{t('dealer_login.register_now')}</Link>
                </div>
              </div>
            </div>

            {/* Info Side */}
            <div className="auth-info-side">
              <div className="info-content">
                <h3 className="info-title">{t('dealer_login.info_title')}</h3>

                <div className="advantage-list">
                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-graph-up-arrow text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_login.analytics_title')}</h4>
                      <p>{t('dealer_login.analytics_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-people text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_login.leads_title')}</h4>
                      <p>{t('dealer_login.leads_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-shield-check text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_login.secure_title')}</h4>
                      <p>{t('dealer_login.secure_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-headset text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_login.support_title')}</h4>
                      <p>{t('dealer_login.support_desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
